import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService } from '@go-modules/services/event/event.service';
import { openManagedMatDialog } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { BehaviorSubject } from 'rxjs';

export interface ConfirmPasswordDialogData {
	user: any;
	sessionId?: string;
	provider: string;
	dialogTitleId?: string;
}

@Component({
	selector: 'confirm-password-dialog',
	templateUrl: './confirm-password-dialog.component.html',
	styleUrls: ['./confirm-password-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ConfirmPasswordDialogComponent implements OnInit {

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public error: any;
	public form: UntypedFormGroup;

	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<ConfirmPasswordDialogData>
	): MatDialogRef<ConfirmPasswordDialogComponent, any> {
		const dialogTitleId = config?.data?.dialogTitleId ?? ConfirmPasswordDialogComponent.getDialogTitleId();

		return openManagedMatDialog(dialog, ConfirmPasswordDialogComponent, {
			...(config ?? {}),
			data: {
				...(config?.data ?? {}),
				dialogTitleId
			},
			ariaLabelledBy: dialogTitleId
		});
	}

	private static getDialogTitleId (): string {
		return `confirm-password-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	}

	constructor (
		public dialogRef: MatDialogRef<ConfirmPasswordDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ConfirmPasswordDialogData,
		@Inject('Window') private window: Window,
		private eventService: EventService,
		private ngxAuthService: NgxAuthService,
		private translate: TranslateService
	) {
		this.data.dialogTitleId ??= ConfirmPasswordDialogComponent.getDialogTitleId();
	}

	public ngOnInit () {
		this.form = new UntypedFormGroup({
			currentPassword: new UntypedFormControl(null, [Validators.required])
		});
	}

	public submit (form) {
		if (form.invalid) {
			return;
		}

		this.loading$.next(true);
		this.ngxAuthService.connectSsoAccount({
			password: form.get('currentPassword').value,
			session: this.data.sessionId
		}, this.data.provider).subscribe({
			next: (response) => {
				this.dialogRef.close(response);
			},
			error: (response) => {
				this.error = null;
				this.loading$.next(false);
				const { status, error } = response;

				switch(status) {
					case 400:
						this.error = this.translate.instant('modal-password-confirm_incorrect-password');
						break;
					case 423:
						const options = {
							accountLockTime: error.remainingAccountLockSeconds
						};
						return this.ngxAuthService.logout(options).then(() => {
							this.dialogRef.close();

							if (this.data.sessionId) {
								this.eventService.broadcast(EVENT_NAMES.ACCOUNT_LOCKED, options);
							} else {
								this.window.location.replace(`https://${this.window.location.host}/dashboard/auth/login/`);
							}
						});
					default:
						this.error = this.translate.instant('unknown-error-description');
				}
			}
		});
	}

	public close () {
		this.dialogRef.close();
	}
}
